import { CREDIT_TERM_OPTIONS, PAYMENT_METHOD_OPTIONS, type TranzilaSettings } from '@/lib/tranzilaSettings';

export interface TranzilaPayment {
  payment_method:         number;
  payment_date:           string;
  cc_credit_term:         number;
  cc_installments_number: number;
  cc_last_4_digits:       string;
  bank:                   string;
  bank_branch:            string;
  bank_account:           string;
  cheque_number:          string;
  paypal_account:         string;
  other_description:      string;
}

export function paymentFromSettings(s: TranzilaSettings, credit: boolean): TranzilaPayment {
  return {
    payment_method:         s.default_payment_method,
    payment_date:           new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Jerusalem' }).format(new Date()),
    cc_credit_term:         s.cc_credit_term,
    cc_installments_number: s.cc_installments,
    cc_last_4_digits:       '',
    bank:                   s.bank_code,
    bank_branch:            s.bank_branch,
    bank_account:           s.bank_account,
    cheque_number:          '',
    paypal_account:         '',
    other_description:      credit ? 'החזר כספי' : s.other_payment_description,
  };
}

export function paymentError(p: TranzilaPayment): string | null {
  if (p.payment_method === 1 && p.cc_last_4_digits && !/^\d{4}$/.test(p.cc_last_4_digits)) return 'יש להזין בדיוק 4 ספרות אחרונות של הכרטיס';
  if (p.payment_method === 1 && p.cc_credit_term !== 1 && !(p.cc_installments_number >= 2 && p.cc_installments_number <= 36)) return 'מספר התשלומים חייב להיות בין 2 ל-36';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(p.payment_date)) return 'יש לבחור תאריך תשלום';
  return null;
}

const inputCls = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-teal-400';
const labelCls = 'block text-xs font-medium text-gray-600 mb-1';

interface Props {
  value:    TranzilaPayment;
  onChange: (next: TranzilaPayment) => void;
  credit:   boolean;
  disabled: boolean;
}

export function TranzilaPaymentFields({ value, onChange, credit, disabled }: Props) {
  const set = <K extends keyof TranzilaPayment>(k: K, v: TranzilaPayment[K]) => onChange({ ...value, [k]: v });
  const m = value.payment_method;

  return (
    <fieldset disabled={disabled} className="space-y-3 border border-gray-100 rounded-xl px-4 py-3">
      <legend className="px-1 text-xs font-semibold text-gray-500">{credit ? 'פרטי ההחזר' : 'פרטי התשלום'}</legend>
      <div className="grid grid-cols-2 gap-3">
        <label className="col-span-2 sm:col-span-1">
          <span className={labelCls}>אמצעי תשלום</span>
          <select value={String(m)} onChange={e => set('payment_method', Number(e.target.value))} className={inputCls}>
            {PAYMENT_METHOD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label className="col-span-2 sm:col-span-1">
          <span className={labelCls}>תאריך</span>
          <input type="date" value={value.payment_date} onChange={e => set('payment_date', e.target.value)} className={inputCls} />
        </label>

        {m === 1 && (
          <>
            <label>
              <span className={labelCls}>סוג עסקה</span>
              <select value={String(value.cc_credit_term)} onChange={e => set('cc_credit_term', Number(e.target.value))} className={inputCls}>
                {CREDIT_TERM_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label>
              <span className={labelCls}>4 ספרות אחרונות</span>
              <input inputMode="numeric" maxLength={4} dir="ltr" value={value.cc_last_4_digits}
                     onChange={e => set('cc_last_4_digits', e.target.value.replace(/\D/g, ''))} className={`${inputCls} text-left`} />
            </label>
            {value.cc_credit_term !== 1 && (
              <label>
                <span className={labelCls}>מספר תשלומים</span>
                <input type="number" min={2} max={36} value={value.cc_installments_number}
                       onChange={e => set('cc_installments_number', Number(e.target.value))} className={inputCls} />
              </label>
            )}
          </>
        )}

        {(m === 3 || m === 4) && (
          <>
            <label>
              <span className={labelCls}>מס' בנק</span>
              <input inputMode="numeric" maxLength={3} value={value.bank} onChange={e => set('bank', e.target.value.replace(/\D/g, ''))} className={inputCls} />
            </label>
            <label>
              <span className={labelCls}>סניף</span>
              <input inputMode="numeric" maxLength={5} value={value.bank_branch} onChange={e => set('bank_branch', e.target.value.replace(/\D/g, ''))} className={inputCls} />
            </label>
            <label>
              <span className={labelCls}>מס' חשבון</span>
              <input inputMode="numeric" maxLength={20} value={value.bank_account} onChange={e => set('bank_account', e.target.value.replace(/\D/g, ''))} className={inputCls} />
            </label>
            {m === 3 && (
              <label>
                <span className={labelCls}>מס' צ'ק</span>
                <input inputMode="numeric" maxLength={20} value={value.cheque_number} onChange={e => set('cheque_number', e.target.value.replace(/\D/g, ''))} className={inputCls} />
              </label>
            )}
          </>
        )}

        {m === 6 && (
          <label className="col-span-2">
            <span className={labelCls}>חשבון PayPal</span>
            <input type="email" dir="ltr" maxLength={80} value={value.paypal_account} onChange={e => set('paypal_account', e.target.value)} className={`${inputCls} text-left`} />
          </label>
        )}

        {m === 10 && (
          <label className="col-span-2">
            <span className={labelCls}>תיאור</span>
            <input type="text" maxLength={60} value={value.other_description} onChange={e => set('other_description', e.target.value)} className={inputCls} />
          </label>
        )}
      </div>
    </fieldset>
  );
}
